/**
 * Opportunity Radar (was: What's Trending).
 *
 * User feedback: "the what's trending page isn't working for me. What value
 * does it offer the brand? It's SO DULL."
 *
 * Redesign principles:
 *   1. Nothing generic. Every trend shows a **brand-specific match %** and
 *      **projected lift for THIS brand** (not a global "momentum" number).
 *   2. Competitor-whitespace flag — if a competitor has gone dark on it,
 *      that's a high-value moment. Surface it loudly.
 *   3. Creator readiness — show avatars of the brand's own contracted
 *      creators who've already touched or are predicted to ignite the trend.
 *   4. One-tap "Spawn wave around this trend" → pre-fills Plan Wave.
 *   5. Velocity curve — not a static number, a tiny sparkline so you can
 *      read the trend's trajectory in half a second.
 *   6. Dense, scannable, ranked by brand match desc. No filler copy.
 *
 * Data source: pure client fixture (demo-stable). When we wire the real
 * trend-mining backend we'll swap the `TRENDS` constant for a fetch.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import CreatorAvatar, { CreatorAvatarStack } from '../components/creators/CreatorAvatar';
import {
  Radar, Flame, TrendingUp, TrendingDown, Clock, Zap, Search,
  Target, ShieldAlert, ArrowRight, Activity,
} from 'lucide-react';
import { useDemoProfile } from '../hooks/useDemoProfile';
import { useAuth } from '../contexts/AuthContext';
import LeaderboardStrip from '../components/trending/LeaderboardStrip';

// ─────────────── fallback fixture trends ───────────────
// Used only when caller is NOT a seeded demo brand. Demo brands receive
// vertical-specific trends from /api/brand/demo-profile.
// Scored for the investor-demo brand (Aurora Labs — beauty/skincare).
// Each trend lives in the beauty+wellness whitespace the brand's footprint
// already excels at, with a believable competitor-whitespace angle.
const TRENDS_FALLBACK = [
  {
    id: 'glass-skin-protocol', tag: '#GlassSkinProtocol',
    vertical: 'Beauty / Skincare',
    thesis: 'Glass-skin "morning+evening" dual-routine hook mining your Signature footprint. Aurora-Ritual creators outperform category baseline 2.3×.',
    brand_match: 94, projected_reach: '3.2M', projected_roas: '6.1×', projected_lift: '+38% on last wave',
    momentum: 91, direction: 'up', halflife_hours: 72,
    velocity: [12, 18, 22, 31, 44, 62, 78, 91], // last 8 hours
    competitor_whitespace: { is_open: true, competitor: 'Dermaline', days_dark: 17 },
    ready_creators: [
      { handle: '@ninaroutines',   platform: 'TikTok' },
      { handle: '@dr.melaskin',    platform: 'Instagram' },
      { handle: '@the_auradiary',  platform: 'TikTok' },
      { handle: '@mornings.w.jo',  platform: 'YouTube' },
      { handle: '@lou.the.derm',   platform: 'Instagram' },
      { handle: '@hydratedbymia',  platform: 'TikTok' },
    ],
    preset: { wave_type: 'Performance', goal: 'Conversion', platforms: ['TikTok', 'Instagram'] },
  },
  {
    id: 'hello-aura-sound', tag: '♪ "Hello Aura" (sound)',
    vertical: 'Sound / Audio',
    thesis: '2.3× velocity WoW. 4 of your contracted creators used it organically in last 48h — algorithm already primes your brand-adjacent recommendations.',
    brand_match: 88, projected_reach: '2.7M', projected_roas: '4.9×', projected_lift: '+24% reach vs stock hook',
    momentum: 87, direction: 'up', halflife_hours: 36,
    velocity: [5, 9, 14, 22, 35, 51, 70, 87],
    competitor_whitespace: { is_open: false, competitor: 'Petal & Fern', days_dark: 0 },
    ready_creators: [
      { handle: '@ninaroutines',  platform: 'TikTok' },
      { handle: '@the_auradiary', platform: 'TikTok' },
      { handle: '@mornings.w.jo', platform: 'YouTube' },
      { handle: '@skin.byizzy',   platform: 'TikTok' },
    ],
    preset: { wave_type: 'Branding', goal: 'Awareness', platforms: ['TikTok'] },
  },
  {
    id: 'hyperpigment-confessions', tag: '#HyperpigmentConfessions',
    vertical: 'Beauty / Skincare',
    thesis: 'Confession-style POV hook for pigment products. Niche audience (your exact ICP) — low competition, high relevance.',
    brand_match: 86, projected_reach: '1.4M', projected_roas: '5.4×', projected_lift: '+41% engagement',
    momentum: 73, direction: 'up', halflife_hours: 120,
    velocity: [8, 12, 18, 24, 35, 48, 62, 73],
    competitor_whitespace: { is_open: true, competitor: 'The Basics Lab', days_dark: 9 },
    ready_creators: [
      { handle: '@the_auradiary', platform: 'TikTok' },
      { handle: '@dr.melaskin',   platform: 'Instagram' },
      { handle: '@lou.the.derm',  platform: 'Instagram' },
    ],
    preset: { wave_type: 'Performance', goal: 'Conversion', platforms: ['TikTok', 'Instagram'] },
  },
  {
    id: 'retail-footfall-glowmart', tag: '📍 Glowmart NYC+Miami footfall +14%',
    vertical: 'Retail signal',
    thesis: "Your retail footprint pulled 14% more foot traffic this week. A geo-wave (NYC + Miami DMA) will compound in-store conversion.",
    brand_match: 91, projected_reach: '2.1M', projected_roas: '5.8×', projected_lift: '+19% in-store SKU pull',
    momentum: 82, direction: 'up', halflife_hours: 168,
    velocity: [22, 30, 38, 47, 58, 66, 74, 82],
    competitor_whitespace: { is_open: false, competitor: '—', days_dark: 0 },
    ready_creators: [
      { handle: '@ninaroutines',   platform: 'TikTok' },
      { handle: '@mornings.w.jo',  platform: 'YouTube' },
      { handle: '@skin.byizzy',    platform: 'TikTok' },
      { handle: '@hydratedbymia',  platform: 'TikTok' },
    ],
    preset: { wave_type: 'Performance', goal: 'Conversion', platforms: ['TikTok', 'Instagram'] },
  },
  {
    id: 'fading-minimalist', tag: '#MinimalistSkincare',
    vertical: 'Beauty / Skincare',
    thesis: "Has peaked. Past-peak waves historically deliver 0.7× ROAS — skip this one and save the budget.",
    brand_match: 34, projected_reach: '500K', projected_roas: '1.2×', projected_lift: '—',
    momentum: 28, direction: 'down', halflife_hours: 18,
    velocity: [62, 58, 51, 44, 38, 31, 30, 28],
    competitor_whitespace: { is_open: false, competitor: '—', days_dark: 0 },
    ready_creators: [],
    preset: null,
  },
];

// ─────────────── page ───────────────
export default function WhatsTrendingPage() {
  const { profile, isDemo } = useDemoProfile();
  // Use demo-profile trends if available (brand-specific), else fallback fixture.
  const TRENDS = (isDemo && profile?.trends?.length) ? profile.trends : TRENDS_FALLBACK;
  const { user } = useAuth();
  const brandName = user?.brand_name || profile?.brand_name || 'You';
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('match'); // match | momentum | halflife

  const sorted = useMemo(() => {
    const filtered = TRENDS.filter(t =>
      !q || t.tag.toLowerCase().includes(q.toLowerCase()) || t.thesis.toLowerCase().includes(q.toLowerCase())
    );
    if (sort === 'match')    return [...filtered].sort((a, b) => b.brand_match - a.brand_match);
    if (sort === 'momentum') return [...filtered].sort((a, b) => b.momentum - a.momentum);
    return [...filtered].sort((a, b) => a.halflife_hours - b.halflife_hours);
  }, [q, sort, TRENDS]);

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 space-y-6 max-w-[1400px] mx-auto" data-testid="opportunity-radar-page">
        {/* Header */}
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Radar className="h-4 w-4 text-[#FF4D94]" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D94] font-bold">Opportunity Radar</p>
            </div>
            <h1 className="text-3xl font-black text-white tracking-tight leading-tight">
              Trends indexed to <span className="text-[#00D4FF]">your brand</span>.
            </h1>
            <p className="text-xs text-gray-400 mt-1 max-w-prose">
              Not global TikTok charts. Each signal is scored for match against your footprint, checked for competitor whitespace, and pre-wired with the creators you have on roster.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
              <input
                value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search trends…" data-testid="radar-search"
                className="pl-8 pr-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] text-xs text-white placeholder-gray-600 w-56 focus:outline-none focus:border-[#00D4FF]/50"
              />
            </div>
            <SortBtn active={sort === 'match'}    onClick={() => setSort('match')}    label="Match" />
            <SortBtn active={sort === 'momentum'} onClick={() => setSort('momentum')} label="Momentum" />
            <SortBtn active={sort === 'halflife'} onClick={() => setSort('halflife')} label="Halflife" />
          </div>
        </header>

        {/* Live-ticker bar — rotates top 3 urgent signals. Bloomberg vibe. */}
        <LiveTicker items={TRENDS.filter(t => t.direction === 'up' && t.brand_match >= 75).slice(0, 5)} />

        {/* Summary strip */}
        <RadarSummary trends={TRENDS} />

        {/* Trend cards */}
        <div className="space-y-4" data-testid="radar-trend-list">
          {sorted.map(t => <TrendRow key={t.id} trend={t} brandName={brandName} />)}
          {sorted.length === 0 && (
            <div className="text-center text-gray-500 text-xs py-10">No trends match that search.</div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// Compact live ticker — rotates urgency signals every 4s. Zero filler.
function LiveTicker({ items }) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (!items?.length) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items]);
  if (!items?.length) return null;
  const item = items[idx];
  return (
    <div className="rounded-xl px-4 py-3 flex items-center gap-3 overflow-hidden relative"
         data-testid="radar-live-ticker"
         style={{ background: 'linear-gradient(90deg, rgba(255,77,148,0.12) 0%, rgba(15,15,15,0.8) 100%)',
                  border: '1px solid rgba(255,77,148,0.35)' }}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black flex-shrink-0" style={{ background: '#FF4D94', color: '#0A0A0A' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] animate-pulse" /> LIVE
      </span>
      <p className="text-[11px] text-white font-medium flex-1 min-w-0 truncate">
        <span className="text-[#FF4D94] font-bold">{item.tag}</span>
        <span className="text-gray-500 mx-2">·</span>
        <span className="text-gray-300">momentum {item.momentum} · halflife {item.halflife_hours}h · {item.projected_reach} reach projected</span>
        {item.competitor_whitespace.is_open && <>
          <span className="text-gray-500 mx-2">·</span>
          <span className="text-[#FACC15]">{item.competitor_whitespace.competitor} dark {item.competitor_whitespace.days_dark}d</span>
        </>}
      </p>
      <p className="text-[9px] text-gray-500 flex-shrink-0 tabular-nums">{idx + 1}/{items.length}</p>
    </div>
  );
}

function SortBtn({ active, onClick, label }) {
  return (
    <button type="button" onClick={onClick} data-testid={`radar-sort-${label.toLowerCase()}`}
            className="px-3 py-2 rounded-lg text-[10px] uppercase tracking-[0.15em] font-bold transition-colors"
            style={{
              background: active ? '#00D4FF15' : 'transparent',
              color: active ? '#00D4FF' : '#6B7280',
              border: `1px solid ${active ? '#00D4FF50' : '#1f1f1f'}`,
            }}>{label}</button>
  );
}

function RadarSummary({ trends }) {
  const hot = trends.filter(t => t.direction === 'up' && t.brand_match >= 75).length;
  const openWhitespace = trends.filter(t => t.competitor_whitespace.is_open).length;
  const avgMatch = Math.round(trends.reduce((s, t) => s + t.brand_match, 0) / trends.length);
  const expiringSoon = trends.filter(t => t.direction === 'up' && t.halflife_hours <= 48).length;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="radar-summary">
      <SummaryTile icon={Flame}        label="Hot for your brand" value={hot}              color="#FF4D94" sub="brand_match ≥ 75%" />
      <SummaryTile icon={ShieldAlert}  label="Competitor whitespace" value={openWhitespace}  color="#FACC15" sub="rivals are silent" />
      <SummaryTile icon={Target}       label="Average match score"   value={`${avgMatch}%`}  color="#00D4FF" sub="across surfaced trends" />
      <SummaryTile icon={Clock}        label="Act-within-48h"       value={expiringSoon}    color="#A78BFA" sub="short-halflife windows" />
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-xl p-4 bg-[#0F0F0F] border border-[#1f1f1f]">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
      </div>
      <p className="text-2xl font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}

// ─────────────── individual trend card ───────────────
function TrendRow({ trend, brandName }) {
  const isDead = trend.direction === 'down' && trend.momentum < 40;
  const DirIcon = trend.direction === 'up' ? TrendingUp : TrendingDown;
  const dirColor = trend.direction === 'up' ? '#4ade80' : '#FF6B6B';
  const matchColor = trend.brand_match >= 75 ? '#FF4D94' : trend.brand_match >= 50 ? '#FACC15' : '#6B7280';
  // iter 92 — saturation colour tone is owned by the backend payload
  // (`trend.saturation.tone`: 'live' | 'warning' | 'critical' | 'neutral').
  // When absent we degrade gracefully without rendering a fake state.
  const satTone = trend.saturation?.tone || null;
  const satToneColor = {
    live:     '#4ADE80',
    warning:  '#FACC15',
    critical: '#FF4D6B',
    neutral:  '#94A3B8',
  }[satTone] || '#94A3B8';
  return (
    <article
      data-testid={`trend-card-${trend.id}`}
      className="rounded-2xl bg-[#0F0F0F] border overflow-hidden"
      style={{ borderColor: isDead ? '#1f1f1f' : `${matchColor}30`, opacity: isDead ? 0.6 : 1 }}
    >
      <div className="grid lg:grid-cols-[1fr_280px] gap-0">
        {/* LEFT: identity + thesis + creators
            iter 92 — the momentum / halflife chips and the
            "Competitor dark Nd" pill were removed from this header.
            Momentum + Halflife now live in the right panel; competitor
            activity is owned by <CompetitorActivityStrip/> below. */}
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold">{trend.vertical}</p>
              <h3 className="text-xl font-black text-white leading-tight mt-0.5">{trend.tag}</h3>
            </div>
          </div>

          <p className="text-[12px] text-gray-300 leading-relaxed">{trend.thesis}</p>

          {/* Competitor activity — public signal only (iter 92).
              Renders only when at least one uploaded competitor is
              observably active, quiet, or silent on the trend. No
              competitor Pulse, no creator counts, no rank. */}
          <LeaderboardStrip trend={trend} brandName={brandName} />

          {/* Velocity sparkline + Layer 4 halflife projection overlay */}
          <VelocityLine
            values={trend.velocity}
            color={dirColor}
            halflifeHours={trend.halflife_hours}
            halflifeProjection={trend.halflife_projection}
            isDead={isDead}
          />

          {/* Ready creators */}
          {trend.ready_creators.length > 0 && (
            <div className="rounded-lg p-2.5 bg-[#0A0A0A] border border-[#1f1f1f] flex items-center gap-3">
              <CreatorAvatarStack creators={trend.ready_creators.map(c => ({ handle: c.handle }))} max={6} size={26} />
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-white">{trend.ready_creators.length} creators on your roster primed for this</p>
                <p className="text-[9px] text-gray-500 truncate">{trend.ready_creators.slice(0, 3).map(c => c.handle).join(' · ')}{trend.ready_creators.length > 3 ? ' · …' : ''}</p>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: brand-match (hero) + momentum + halflife + saturation
            + CTA.
            iter 92 — replaced the 3 MiniStats (Reach / ROAS / Lift)
            with Momentum + Halflife + Saturation. The previous trio
            were pre-campaign projections that aren't knowable from
            public signal alone:
              • Projected Reach    — requires modelled cohort outcome
              • Projected ROAS     — requires post-campaign revenue
              • Projected Lift     — requires comparison vs prior wave
            All three are now suppressed from this surface entirely. */}
        <aside className="p-5 bg-[#0A0A0A] border-t lg:border-t-0 lg:border-l border-[#1f1f1f] flex flex-col gap-3">
          {/* Brand Match — prominent gauge */}
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-[9px] uppercase tracking-[0.15em] text-gray-500 font-bold">Brand match</p>
              <p
                className="text-3xl font-black tabular-nums leading-none"
                style={{ color: matchColor }}
                data-testid={`trend-${trend.id}-brand-match`}
              >
                {trend.brand_match}%
              </p>
            </div>
            <div className="h-1.5 rounded-full bg-[#1f1f1f] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${trend.brand_match}%`, background: matchColor }} />
            </div>
          </div>

          {/* Momentum + Halflife — observable, no fabrication */}
          <div className="grid grid-cols-2 gap-2">
            <MiniStat
              label="Momentum"
              value={
                <span className="inline-flex items-center gap-0.5">
                  <DirIcon className="h-3 w-3" />
                  {trend.momentum}
                </span>
              }
              color={dirColor}
              testId={`trend-${trend.id}-momentum`}
            />
            <MiniStat
              label="Halflife"
              value={`${trend.halflife_hours}h`}
              color="#00D4FF"
              testId={`trend-${trend.id}-halflife`}
            />
          </div>

          {/* Saturation — qualitative public-volume gauge from the
              backend. Suppressed when the field is missing rather than
              fabricated; tone color is owned by the backend payload. */}
          {trend.saturation && (
            <div
              data-testid={`trend-${trend.id}-saturation`}
              data-tone={satTone}
              className="rounded-lg p-2.5"
              style={{
                background: `${satToneColor}10`,
                border:     `1px solid ${satToneColor}30`,
              }}
            >
              <p
                className="text-[9px] uppercase tracking-[0.15em] font-bold text-gray-500"
              >
                Saturation
              </p>
              <p
                className="text-[13px] font-black mt-0.5"
                style={{ color: satToneColor }}
              >
                {trend.saturation.label}
              </p>
              {trend.saturation.body && (
                <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">
                  {trend.saturation.body}
                </p>
              )}
            </div>
          )}

          {/* Primary CTA */}
          {isDead ? (
            <div className="mt-auto rounded-lg p-2.5 bg-[#FF6B6B10] border border-[#FF6B6B30] text-[10px] text-[#FF6B6B] font-bold text-center">
              Past peak — skip
            </div>
          ) : (
            <Link
              to="/build-wave"
              data-testid={`launch-${trend.id}`}
              state={{ readyFirePreset: trend.preset, readyFireTitle: trend.tag }}
              className="mt-auto inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg text-[11px] font-black hover:brightness-110 transition-all"
              style={{ background: 'transparent', color: '#FF4D94', border: '1px solid rgba(255,77,148,0.45)' }}
            >
              <Zap className="h-3.5 w-3.5" /> Build a wave from this
              <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          )}
        </aside>
      </div>
    </article>
  );
}

function VelocityLine({ values, color, halflifeHours, halflifeProjection, isDead }) {
  const w = 280; const h = 40;
  // Compute (or accept) the dashed halflife projection. Demo brands
  // ship `halflifeProjection` from the backend so it's deterministic
  // and shared with the on-page narrative; non-demo / fallback trends
  // derive it client-side from `halflifeHours` so the overlay still
  // renders consistently.
  const projection = useMemo(() => {
    if (halflifeProjection?.projection?.length) return halflifeProjection;
    if (!halflifeHours || !values?.length || isDead) return null;
    const lastValue = values[values.length - 1];
    const points = [];
    for (let i = 1; i <= 6; i++) {
      const t = halflifeHours * (i / 6);
      const v = lastValue * Math.pow(2, -(t / halflifeHours));
      points.push(Math.round(v * 100) / 100);
    }
    return {
      projection: points,
      horizon_hours: halflifeHours,
      decay_label: `Decays by half ~${halflifeHours}h`,
    };
  }, [values, halflifeHours, halflifeProjection, isDead]);

  // Real velocity segment occupies the LEFT 60% of the chart; the
  // dashed halflife projection (Layer 4) occupies the RIGHT 40% so the
  // brand can read past + future on one axis. When no projection is
  // available, the real velocity fills the full width (legacy mode).
  const hasProjection = !!projection && projection.projection.length > 0;
  const realWidthRatio = hasProjection ? 0.6 : 1.0;
  const realWidth = w * realWidthRatio;
  // Combined max for shared Y-axis so the dashed line continues at the
  // visually correct height (no jump at the boundary).
  const combinedMax = Math.max(
    ...(values || [1]),
    ...(hasProjection ? projection.projection : []),
    1,
  );

  const stepReal = realWidth / Math.max(1, values.length - 1);
  const realD = values.map((v, i) =>
    `${i === 0 ? 'M' : 'L'} ${i * stepReal},${h - (v / combinedMax) * h}`,
  ).join(' ');
  const fillD = `${realD} L ${realWidth},${h} L 0,${h} Z`;

  // Projection segment — starts at the last real point, then walks
  // through the 6 decay points across the right 40% of the chart.
  let projD = '';
  let projEndX = realWidth;
  let projEndY = h;
  if (hasProjection) {
    const projPts = projection.projection;
    const projWidth = w - realWidth;
    const stepProj = projWidth / projPts.length;
    const lastReal = values[values.length - 1] || 0;
    const startY = h - (lastReal / combinedMax) * h;
    projD = `M ${realWidth},${startY}`;
    projPts.forEach((v, i) => {
      const x = realWidth + (i + 1) * stepProj;
      const y = h - (v / combinedMax) * h;
      projD += ` L ${x},${y}`;
      if (i === projPts.length - 1) {
        projEndX = x;
        projEndY = y;
      }
    });
  }

  return (
    <div
      className="rounded-lg p-2 bg-[#0A0A0A] border border-[#1f1f1f]"
      data-testid="velocity-sparkline"
      data-has-projection={hasProjection ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-500 font-bold flex items-center gap-1">
          <Activity className="h-3 w-3" /> 8h engagement velocity
          {hasProjection && (
            <span
              className="ml-2 inline-flex items-center gap-1 text-[8.5px] normal-case tracking-normal font-bold"
              style={{ color: '#FACC15' }}
              data-testid="halflife-projection-label"
            >
              <span
                className="inline-block w-3 h-0"
                style={{ borderTop: `1.5px dashed #FACC15` }}
                aria-hidden
              />
              {projection.decay_label}
            </span>
          )}
        </p>
        <p className="text-[9px] text-gray-500">posts/hr</p>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none">
        {/* Vertical divider between past + projection */}
        {hasProjection && (
          <line
            x1={realWidth} y1="0" x2={realWidth} y2={h}
            stroke="#FACC15" strokeOpacity="0.18" strokeWidth="0.5"
            strokeDasharray="2 3"
          />
        )}
        {/* Real velocity area + line */}
        <path d={fillD} fill={color} opacity="0.15" />
        <path d={realD} stroke={color} strokeWidth="1.5" fill="none" />
        {/* Dashed halflife projection */}
        {hasProjection && (
          <>
            <path
              d={projD}
              stroke="#FACC15"
              strokeWidth="1.4"
              strokeDasharray="3 3"
              fill="none"
              opacity="0.85"
              data-testid="halflife-projection-line"
            />
            {/* Small dot anchoring the end of the projection */}
            <circle cx={projEndX} cy={projEndY} r="2" fill="#FACC15" opacity="0.85" />
          </>
        )}
      </svg>
    </div>
  );
}

function MiniStat({ label, value, color = '#ffffff', wide, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg p-2 bg-[#0F0F0F] border border-[#1f1f1f] ${wide ? 'col-span-2' : ''}`}
    >
      <p className="text-[8.5px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
      <p className="text-sm font-black tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
